import React, { useState, useEffect, useMemo, useRef } from 'react';
import { selectionAPI, authAPI, productsAPI } from '../api/client';
import { useAuthStore } from '../store';

// Поиск + список чекбоксов
function CheckList({ options, selected, onChange, placeholder = 'Поиск…', maxHeight = 200 }) {
  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);
  const inputRef = useRef(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter(o => o.toLowerCase().includes(q)) : options;
  }, [options, query]);

  const toggle = (val) => {
    if (selected.includes(val)) onChange(selected.filter(x => x !== val));
    else onChange([...selected, val]);
  };

  return (
    <div className="cklist">
      <div className="cklist-search">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="cklist-icon">
          <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
        <input
          ref={inputRef}
          className="cklist-input"
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onFocus={() => setFocused(true)}
          placeholder={placeholder}
          autoComplete="off"
        />
        {query && (
          <button type="button" className="cklist-clear" onClick={() => { setQuery(''); inputRef.current?.focus(); }}>×</button>
        )}
      </div>

      {selected.length > 0 && (
        <div className="cklist-tags">
          {selected.map(s => (
            <span key={s} className="cklist-tag">
              {s}<button type="button" className="cklist-tag-x" onClick={() => toggle(s)}>×</button>
            </span>
          ))}
        </div>
      )}

      {(focused || selected.length > 0) && <div className="cklist-list" style={{ maxHeight }}>
        {filtered.length === 0 && <div className="cklist-empty">Ничего не найдено</div>}
        {filtered.map(opt => (
          <label key={opt} className={`cklist-item${selected.includes(opt) ? ' cklist-item--checked' : ''}`}>
            <input type="checkbox" checked={selected.includes(opt)} onChange={() => toggle(opt)} className="cklist-checkbox" />
            <span>{opt}</span>
          </label>
        ))}
      </div>}

      {(focused || selected.length > 0) && options.length > 0 && (
        <div className="cklist-footer">
          <button
            type="button"
            className="cklist-footer-btn"
            onClick={() => onChange([...new Set([...selected, ...filtered])])}
            disabled={filtered.length === 0}
          >
            Выбрать все {filtered.length < options.length ? `"${query.trim()}" ` : ''}({filtered.length})
          </button>
          {selected.length > 0 && (
            <button type="button" className="cklist-footer-btn cklist-footer-btn--clear" onClick={() => onChange([])}>
              Снять все ({selected.length})
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// Кнопки размеров (одиночный выбор)
function SizeButtons({ sizes, value, onChange }) {
  if (!sizes.length) return null;
  return (
    <div className="size-btns">
      {sizes.map(s => (
        <button
          key={s}
          type="button"
          className={`size-btn${value === s ? ' size-btn--active' : ''}`}
          onClick={() => onChange(value === s ? '' : s)}
        >
          {s}
        </button>
      ))}
    </div>
  );
}

export default function UserSelectionModal({ onClose, initialFilters }) {
  const storeUser = useAuthStore(s => s.user);
  const updateUser = useAuthStore(s => s.updateUser);
  const [tgId, setTgId] = useState(storeUser?.tg_id || null);

  const [meta, setMeta]       = useState(null);
  const [metaErr, setMetaErr] = useState(false);
  const [allSizes, setAllSizes] = useState([]);

  // Начальное состояние из фильтров каталога (если открыто с кнопки в size-bar)
  const [models,   setModels]   = useState(
    initialFilters?.model ? [initialFilters.model] : []
  );
  const [size,     setSize]     = useState(initialFilters?.sizes?.[0] || '');
  const [size2,    setSize2]    = useState(
    initialFilters?.allSizes && initialFilters?.sizes?.[1] ? initialFilters.sizes[1] : ''
  );
  const [maxPrice, setMaxPrice] = useState(initialFilters?.maxSupplierPrice || '');
  const [search,   setSearch]   = useState(initialFilters?.search || '');
  const [supplier, setSupplier] = useState('');

  const [error,   setError]   = useState('');

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  useEffect(() => {
    authAPI.getMe()
      .then(r => {
        const freshTgId = r.data?.tg_id || null;
        setTgId(freshTgId);
        if (freshTgId !== storeUser?.tg_id) updateUser({ tg_id: freshTgId });
      })
      .catch(() => {});

    selectionAPI.getMetaUser()
      .then(r => setMeta(r.data))
      .catch(() => setMetaErr(true));

    productsAPI.getSizes()
      .then(r => setAllSizes(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Если передан бренд из каталога и модель не выбрана — подставляем все модели этого бренда
  useEffect(() => {
    if (!meta || !initialFilters?.brand || models.length > 0) return;
    const brandModels = meta.brands_models[initialFilters.brand] || [];
    if (brandModels.length > 0) setModels(brandModels);
  }, [meta]); // eslint-disable-line react-hooks/exhaustive-deps

  const availableModels = useMemo(() => {
    if (!meta) return [];
    const set = new Set();
    Object.values(meta.brands_models).forEach(ms => ms.forEach(m => set.add(m)));
    return [...set].sort();
  }, [meta]);

  const hasFilters = models.length || size || size2 || maxPrice || search || supplier;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!hasFilters) { setError('Укажите хотя бы один фильтр'); return; }
    const payload = {
      models:    models.length  ? models  : null,
      size:      size  || null,
      size2:     size2 || null,
      max_price: maxPrice ? parseFloat(maxPrice) : null,
      search:    search.trim()   || null,
      supplier:  supplier.trim() || null,
    };
    onClose(); // закрываем сразу, отправка в фоне
    selectionAPI.sendMe(payload).catch(err => {
      const msg = err?.response?.data?.detail || 'Ошибка отправки подборки';
      window.alert(msg);
    });
  };

  const handleReset = () => {
    setModels([]); setSize(''); setSize2('');
    setMaxPrice(''); setSearch(''); setSupplier('');
    setError('');
  };

  return (
    <div className="usel-overlay" onClick={onClose}>
      <div className="usel-modal" onClick={e => e.stopPropagation()}>
        <div className="usel-header">
          <h2 className="usel-title">Подборка</h2>
          <button className="usel-close" onClick={onClose} title="Закрыть">×</button>
        </div>

        {!tgId && (
          <div className="usel-no-tg">
            Чтобы получить подборку в Telegram, привяжите аккаунт в настройках профиля.
          </div>
        )}

        <p className="usel-hint">Выберите фильтры — бот пришлёт фото подходящих товаров в Telegram.</p>

        {metaErr ? (
          <div style={{ color: 'var(--lg-red)', padding: '12px 0' }}>Ошибка загрузки данных</div>
        ) : !meta ? (
          <div style={{ color: 'var(--lg-text-3)', padding: '12px 0' }}>Загрузка…</div>
        ) : (
          <form className="usel-form" onSubmit={handleSubmit}>

            {/* Модели */}
            <div className="usel-cl-col">
              <div className="usel-cl-label">Модель <span className="usel-count">({availableModels.length})</span></div>
              <CheckList
                options={availableModels}
                selected={models}
                onChange={setModels}
                placeholder="Поиск модели…"
                maxHeight={240}
              />
            </div>

            {/* Размеры кнопками */}
            {allSizes.length > 0 && (
              <div className="usel-sizes-block">
                <div className="usel-size-row">
                  <span className="usel-size-label">Размер</span>
                  <SizeButtons sizes={allSizes} value={size} onChange={setSize} />
                </div>
                <div className="usel-size-row">
                  <span className="usel-size-label">Доп. размер</span>
                  <SizeButtons sizes={allSizes} value={size2} onChange={setSize2} />
                </div>
              </div>
            )}

            {/* Остальные фильтры */}
            <div className="usel-grid">
              <label className="usel-field">
                <span>Поставщик</span>
                <input
                  type="text"
                  list="usel-suppliers"
                  value={supplier}
                  onChange={e => setSupplier(e.target.value)}
                  placeholder="начните вводить…"
                  autoComplete="off"
                />
                <datalist id="usel-suppliers">
                  {meta.suppliers.map(s => <option key={s} value={s} />)}
                </datalist>
              </label>

              <label className="usel-field">
                <span>Поиск в названии / артикуле</span>
                <input
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Gore-tex, ALL12345…"
                />
              </label>

              <label className="usel-field">
                <span>Макс. цена</span>
                <input
                  type="number"
                  value={maxPrice}
                  onChange={e => setMaxPrice(e.target.value.replace(',', '.'))}
                  placeholder="5000"
                  min="0"
                />
              </label>
            </div>

            <div className="usel-actions">
              <button
                type="submit"
                className="usel-btn usel-btn--primary"
                disabled={!tgId}
                title={!tgId ? 'Привяжите Telegram в настройках' : undefined}
              >
                Отправить в Telegram
              </button>
              <button type="button" className="usel-btn usel-btn--ghost" onClick={handleReset}>
                Сбросить
              </button>
            </div>

            {error && <div className="usel-alert usel-alert--err">{error}</div>}
          </form>
        )}
      </div>
    </div>
  );
}
